/**
 *  * *******************************************************************************************************
 ** * V1.7
 *
 * User: danielr
 * Date: 17/12/13
 *
 * tooltip alignment feature.
 * options:
 * - align [Tooltip.ALIGN] optional - default: Tooltip.ALIGN.MIDDLE
 *  attribute name: tooltip-align
 *  possible values: middle (default), top, right, bottom, left
 *
 *  * *******************************************************************************************************
 *
 * V1.6
 *
 * User: shlomif
 * Date: 04/12/13
 *
 * The text option is now escaped for html by calling secureUtilities.escapeForHtmlContent. Only br elements
 * are allowed. Make sure you use one of the following two ways to represent br element: <br/> OR <br />
 *
 * * *******************************************************************************************************
 */

/**
 * * * *  * *******************************************************************************************************
 * * V1.6
 *
 * User: danielr
 * Date: 14/01/14
 *
 * use of the PositionUtils position() method for the positioning
 *  * *******************************************************************************************************
 *
 * V1.5
 *
 * User: danielr
 * Date: 19/11/13
 *
 * Line breaks added.
 * If line breaks needed, add &#13; in the tooltip text
 *
 * tooltip delay added as an option. optional. default is set to 500 (ms). attribute: tooltip-delay
 * tooltip hide attribute if decided to not to show the tooltip. example: the tooltip should be displayed only once
 *
 * * *******************************************************************************************************
 *
 * V1.4
 *
 * User: danielr
 * Date: 29/7/13
 *
 * In this version, the on demand feature is OBSOLETE.
 * The on demand tooltip is supported by the Popover component.
 *
 * * *******************************************************************************************************
 *
 * V1.3
 *
 * User: danielr
 * Date: 6/4/13
 * Visual PDF: https://drive.google.com/a/liveperson.com/?pli=1#folders/0BzCszeQVU73yUU8wdVdsMW41Qzg
 *
 * The component lives at the level of the application.
 * This means that the LE.tooltip instance is accessible to all the modules. No need to instantiate.
 * Requirement in this version:
 * - Only one tooltip at a time is visible
 * - If no position of the tooltip is specified, the default position is at the bottom of the element
 * - The Tooltip component is responsible to hide the tooltip only when the mouseleave event is trigerred in the element
 * The tooltip widget has two modes.
 * on demand and scanning modes.
 *
 * ### on demand
 * the Tooltip component exposes the show method to display the tooltip.
 * the following options are available:
 * - el [backbone el] mandatory - the reference to the element to which you want to apply the tooltip
 * - text [string] optional - if not specified, will get the text in the title attribute of the element
 * - position [LE.tooltip.POSITION] optional default:BOTTOM - the position of the tooltip in regards the element
 * - theme [LE.tooltip.THEME] optional default:[none] - the theme of the tooltip (use DARK if your region is light)
 * - icon [LE.tooltip.ICON] optional default:[none] - the icon to apply next to the tooltip text
 *
 * example:
 * LE.tooltip.show(
 * {el:$(ev.currentTarget),
 * position:LE.tooltip.POSITION.RIGHT,
 * text: "my tooltip"});
 *
 * ### scanning
 * the tooltip, when initialized scans the whole page for elements with a title attribute.
 * the tooltip will apply the following events for the matching elements:
 * - mouseenter (displays the tooltip at the bottom)
 * - mouseleave (hides the tooltip)
 *
 * In order to avoid to have both the browser default tooltip and the nice tooltip displayed at the same time, we temporary remove the title attribute from the el when the tooltip is displayed. Once the tooltip is hidden, we place back the title attribute to the el.
 * This will not affect the accessibility of the element:
 * http://stevenclark.com.au/2008/06/30/title-attributes-on-links-tooltips-and-accessibility/
 *
 * it's also possible to customize the tooltip at the level of the el by adding tooltip-specific attributes:
 *
 * example i want to apply a dark theme error icon tooltip displayed in the right:
 *
 * <div title="i'm a lovely tooltip" tooltip-position="right" tooltip-icon="error" tooltip-theme="dark">
 * show me my tooltip
 * </div>
 *
 * ### features
 * - the tooltip will automatically adjust its position if not enough room available in the initial position
 * - the tooltip will automatically apply a DARK theme if the ".dark" class is defined at an higher level in the DOM
 * - the tooltip is a singleton element. rendered only once. everytime a tooltip needs to be displayed we just modify the css properties of the tooltip
 *
 * This is the responsability of the developer to use the tooltip efficiently. too many title attributes can be very annoying for the user.
 * http://www.rnib.org.uk/professionals/webaccessibility/wacblog/Lists/Posts/Post.aspx?id=38
 *
 * ### future versions
 * - html support
 * - customizable tooltip delay
 *
 * ### issues (minor)
 * - we could not figure out how to dynamically add a new region for the tooltip to the main layout. currently done with jquery:
 * $("<div></div>").attr({ class: "tooltipContainer" }).appendTo(".le");
 * - everytime we display a tooltip we still call the render() method of the view. We should do it once only but for some reasons, the position() method
 * of the jqueryui does not work correctly...
 *
 * *******************************************************************************************************
 */
define(function (require) {
    "use strict";
    var $ = require("jquery");
    var Logger = require('logger');
    var TooltipModel = require('ui/tooltip/js/models/TooltipModel');
    var TooltipView = require('ui/tooltip/js/views/TooltipView');
    var _ = require("underscore");

    var defaultOptions = {};
    var options = {};

    var Tooltip = (function () {
        var clickedTooltipTarget = null;

        var DEFAULT_TOOLTIP_DELAY = 500;
        var DEFAULT_TOOLTIP_POSITION = "bottom";
        var DEFAULT_TOOLTIP_ALIGN = "middle";

        var ver = "1.6";
        var initialized = false;

        var POSITION = {
            TOP: "top",
            RIGHT: "right",
            BOTTOM: "bottom",
            LEFT: "left"
        };

        var ALIGN ={
            MIDDLE: "middle",
            LEFT: "left",
            RIGHT: "right",
            TOP: "top",
            BOTTOM: "bottom"
        };

        var config = {
            /***
             * public supported parameters and attributes
             */
            options : {
                el : "el",    //mandatory - target el to which the tooltip will be applied
                text: "text", //optional - tooltip text
                position : {  //optional
                    param: "position",
                    values: {
                        top: POSITION.TOP,
                        right: POSITION.right,
                        bottom: POSITION.BOTTOM,
                        left: POSITION.LEFT
                    }
                },
                icon : {  //optional - the tooltip icon
                    param: "icon",
                    values: {
                        error: "error"
                    }
                },
                theme : {  //optional - the tooltip theme
                    param: "theme",
                    values: {
                        dark: "dark"
                    }
                },
                align: { //optional - the tooltip alignment
                    param: "align",
                    values: {
                        middle: ALIGN.middle,
                        top: ALIGN.TOP,
                        right: ALIGN.RIGHT,
                        bottom: ALIGN.BOTTOM,
                        left: ALIGN.LEFT
                    }
                },
                tooltipDelay: "tooltipDelay"
            },
            cls: {
                region: {
                    dark: "div.dark"
                },
                theme: {
                    dark: "dark"
                },
                icon: {
                    error: "error"
                }
            },
            POSITION : POSITION,
            ALIGN : ALIGN,
            attributePosition : "tooltip-position",
            attributeIcon : "tooltip-icon",
            attributeTheme : "tooltip-theme",
            attributeHideTooltip: "tooltip-hide",
            attributeTooltipDelay: "tooltip-delay",
            attributeTooltipAlign: "tooltip-align",
            attributeText : "tooltip-text"
        };

        var o = config.options;

        var logger = Logger.getLogger("Tooltip");
        var tooltipModel = new TooltipModel({});

        /****
         * initialize: will define the mouseenter and mouseleave events for the non-on demand tooltip (page scanning)
         * for elements with a title attribute
         * @param opt: eventually can get options to extend to the default options
         * @return {*}
         */
        var initialize = function(opt){
            logger.debug("Tooltip::initialize - starting");
            defaultOptions = {el: {}, text:"", position: DEFAULT_TOOLTIP_POSITION, tooltipDelay: DEFAULT_TOOLTIP_DELAY, align: DEFAULT_TOOLTIP_ALIGN};
            options = $.extend({}, defaultOptions, opt );

            /***
             * this will define the mouseenter event for all the elements with a title attribute
             */
            $(".root").on("mouseenter", "a[title], div[title], span[title], img[title], p[title],li[title], button[title], tr[title], iframe[title]", function () {
                clickedTooltipTarget = null;
                var $that = $(this);
                if (!_.isEmpty($that)) {
                    var title = $that.attr("title");
                    var doNotDisplayTooltip = $that.attr(config.attributeHideTooltip);
                    if (title !== "" && _.isUndefined(doNotDisplayTooltip)) {
                        options[o.text] = title;
                        options[o.el] = $that;
                        var pos = $that.attr(config.attributePosition);
                        var icon = $that.attr(config.attributeIcon);
                        var theme = $that.attr(config.attributeTheme);
                        var align = $that.attr(config.attributeTooltipAlign);
                        var tooltipDelay = $that.attr(config.attributeTooltipDelay);
                        var positioning = initializePositioning(_.isUndefined(pos) ? DEFAULT_TOOLTIP_POSITION : pos, _.isUndefined(align) ? DEFAULT_TOOLTIP_ALIGN : align);
                        options[o.position.param] = positioning.pos;
                        options[o.icon.param] = icon;
                        options[o.theme.param] = theme;
                        options[o.align.param] = positioning.align;
                        options[o.tooltipDelay] = _.isUndefined(tooltipDelay) ? DEFAULT_TOOLTIP_DELAY : tooltipDelay;
                        logger.debug("preparing to displaying tooltip - title: " + title + " - config: " + options);
                        _display();
                    }
                }
            });

            /***
             * this will define the mouseleave event for all the elements with a title attribute (hides the tooltip)
             */
            $(".root").on("mouseleave", "a[title], div[title], span[title], img[title], p[title],li[title], button[title], tr[title], iframe[title]", function(){
                options[o.el] = $(this);
                hide();});

            /***
             * this will act like the mouseleave event for all the buttons elements with a title attribute clicked (hides the tooltip). see defect CFE-1219
             * update: fix for LE-80659: move the tooltip away instead of hiding it
             */
            $(".root").on("mousedown", "button[title], a[title], div[title]", function(){
                clickedTooltipTarget = $(this);
                var tooltip = _tooltipView.getInstance();
                tooltip.moveTooltipAway();
            });

            return Tooltip;
        };

        var initializePositioning = function(pos, align){
            logger.debug("Tooltip::initializePositioning - starting");
            var result = {pos: pos, align: align};
            if (align !== DEFAULT_TOOLTIP_ALIGN){
                if ((align === config.ALIGN.RIGHT || align === config.ALIGN.LEFT) && (pos === config.POSITION.LEFT || pos === config.POSITION.RIGHT)){
                    result.pos = config.POSITION.BOTTOM;
                }
                else if ((align === config.ALIGN.TOP || align === config.ALIGN.BOTTOM) && (pos === config.POSITION.TOP || pos === config.POSITION.BOTTOM)){
                    result.pos = config.POSITION.RIGHT;
                }
            }

            return result;
        };

        /****
         * _display: displays the tooltip. is called once the options for the tooltip is defined
         * @private
         */
        var _display = function(){

            var targetElement = options[o.el];
            if (!_.isEmpty(targetElement)){
                var tooltip = _tooltipView.getInstance();
                tooltip.setViewProperties(options, config);

                /***
                 * removes temporary the default title attribute in order to avoid two tooltips to be shown (the browser default and the nice one)
                 * stores the title text in an extra attribute in order to set it back when tooltip is hidden
                 */

                tooltipModel.set("text", options.text.replace(/\r/g,"<br />"));
                if (_.isFunction(targetElement.attr) && targetElement.attr("title")) {
                    logger.debug("Tooltip::_display - removing the title attribute");
                    targetElement.attr(config.attributeText, targetElement.attr("title"));
                    targetElement.attr("title", "");
                }

                /**
                 * (re)defines the mouseleave event (not defined if on demand)
                 */
                //itai > check to use once/unique so that we do not need to care to remove the handler on _hide
                /*$(targetElement).on("mouseleave, click", function(){
                 targetElement = $(this);
                 });*/


                /***
                 * apply a delay before displaying the tooltip
                 */
                if (_.isFunction(targetElement.clearQueue)){
                    targetElement.clearQueue().delay(options[o.tooltipDelay]).queue(function () {
                        //TODO: needs to check how to avoid calling tooltip.render everytime as the tooltip is already rendered
//                if (firstTime) {
//                    tooltip.render();
//                }
//                else
//                {
//                    tooltip.showTooltip();
//                }
                        var renderTooltip = true;
                        var clickedTooltipTargetIsJquery = clickedTooltipTarget instanceof $;
                        if (clickedTooltipTargetIsJquery) {
                            renderTooltip = clickedTooltipTarget.length > 0 && options[o.el].length > 0 && clickedTooltipTarget.get(0) !== options[o.el].get(0);
                        }
                        if (renderTooltip){
                            logger.debug("Tooltip::_display - before rendering the tooltip");
                            tooltip.render();
                            targetElement.dequeue();
                        }
                    });
                }
            }
        };

        /****
         * _hide: hides the tooltip. not on demand. it is hidden on mouseleave on the el only
         * @private
         */
        var hide = function () {

            logger.debug("Tooltip::hide - starting");
            var targetElement = options[o.el];
            if (!_.isEmpty(targetElement) && _.isFunction(targetElement.attr)){
                /***
                 * set back the title attribute
                 */
                var attributeText = targetElement.attr(config.attributeText);
                if (attributeText && attributeText !== ""){
                    logger.debug("Tooltip::hide - set back the original title attribute");
                    targetElement.attr("title", attributeText);
                }

                /**
                 * hides the tooltip
                 */
                var tooltip = _tooltipView.getInstance();
                if (_.isFunction(targetElement.dequeue)){
                    targetElement.dequeue();
                }
                logger.debug("Tooltip::hide - before calling hideTooltip");
                tooltip.hideTootltip();
            }
        };

        /***
         * _tooltipView: tooltip view singleton
         * @type {*}
         * @private
         */
        var _tooltipView = (function () {
            var instance;

            return {
                getInstance: function () {
                    if ( !instance ) {
                        //TODO: check if we can use a layout instead
                        $("<div></div>").addClass("tooltipContainer").appendTo(".root");

//                        host.addRegions({tooltipContainer:".tooltipContainer"});
                        instance = new TooltipView({model: tooltipModel});
//                        host.tooltipContainer.show(instance);

                        initialized = true;
                    }
                    return instance;
                }
            };
        })();

        initialize({});

        return {
//            initialize: initialize,
            version : ver,
            hide: hide,
            POSITION: POSITION,
            ALIGN: ALIGN
        };

    }());

    return Tooltip;

});
